import { motion } from "framer-motion";
import { TimelineIcon } from "./WeatherScene";
import { useSettings } from "@/lib/settings";
import { formatTemp } from "@/lib/units";

/**
 * SixHourTimeline — compact, human-readable strip of the next 6 hours.
 * Hour chip = big rounded square with weather icon + temperature.
 * Below each chip: a soft precipitation bar (0-100% of max) + label if >0.2 mm.
 *
 * Phase 58 — When the multi-model band shows ≥1.5°C spread, we render
 * a small "low-high" range hint below the temperature so users SEE the
 * uncertainty instead of trusting a fake single-decimal number.
 */
export default function SixHourTimeline({ hourly, theme }) {
  const { settings } = useSettings();
  const times = (hourly?.time || []).slice(0, 6);
  const temps = (hourly?.temperature_2m || []).slice(0, 6);
  const precip = (hourly?.precipitation || []).slice(0, 6);
  const codes = (hourly?.weather_code || []).slice(0, 6);
  const bandMin = (hourly?.temperature_2m_band_min || []).slice(0, 6);
  const bandMax = (hourly?.temperature_2m_band_max || []).slice(0, 6);
  const spread = (hourly?.temperature_2m_band_spread || []).slice(0, 6);

  if (!times.length) {
    return (
      <div
        className="flex justify-between gap-2 py-4 px-1"
        data-testid="timeline-loading"
        style={{ color: theme.textMuted }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 rounded-2xl h-24"
            style={{
              background: theme.surface,
              border: `1px solid ${theme.surfaceBorder}`,
            }}
          />
        ))}
      </div>
    );
  }

  const peak = Math.max(0.4, ...precip.map((v) => v || 0));

  return (
    <div
      className="flex justify-between gap-1.5"
      data-testid="six-hour-timeline"
    >
      {times.map((iso, i) => {
        const d = new Date(iso);
        const hourLabel = d.toLocaleTimeString([], {
          hour: "numeric",
          hour12: true,
        });
        const t = temps[i];
        const p = precip[i] || 0;
        const barPct = Math.min(100, (p / peak) * 100);
        const hasRain = p >= 0.1;
        const isNow = i === 0;
        // Show a "low–high" range hint only when models meaningfully disagree
        // (≥1.5°C spread). Below that the band is too tight to render
        // legibly inside the slot.
        const sp = spread?.[i];
        const showBand =
          sp != null && sp >= 1.5 && bandMin?.[i] != null && bandMax?.[i] != null;
        return (
          <div
            key={iso}
            className="relative flex-1 flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-2xl min-w-0"
            style={{
              background: isNow
                ? `linear-gradient(180deg, ${theme.accentSoft} 0%, ${theme.surface} 100%)`
                : theme.surface,
              border: `1px solid ${isNow ? theme.accent : theme.surfaceBorder}`,
              borderWidth: isNow ? 1.5 : 1,
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: isNow ? `0 4px 14px ${theme.accent}33` : "none",
            }}
            data-testid={`timeline-slot-${i}`}
          >
            {isNow && (
              <motion.span
                className="absolute top-1 right-1 inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: theme.accent }}
                animate={{ opacity: [0.35, 1, 0.35], scale: [1, 1.25, 1] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                aria-hidden="true"
              />
            )}
            <span
              className="text-[10px] font-semibold tracking-wide"
              style={{
                color: isNow ? theme.accent : theme.textMuted,
                letterSpacing: isNow ? "0.05em" : "normal",
              }}
            >
              {isNow ? "NOW" : hourLabel}
            </span>
            <TimelineIcon code={codes[i]} size={22} theme={theme} />
            <span
              className="text-[13px] font-semibold tabular-nums"
              style={{ color: theme.textPrimary }}
            >
              {t != null ? formatTemp(t, settings.tempUnit) : "—"}
            </span>
            {/* Range hint — only when models disagree enough to matter */}
            {showBand && (
              <span
                className="text-[9px] tabular-nums leading-none"
                style={{ color: theme.textMuted, marginTop: -2 }}
                data-testid={`timeline-band-${i}`}
                title={`Models range: ${formatTemp(bandMin[i], settings.tempUnit)} to ${formatTemp(bandMax[i], settings.tempUnit)}`}
              >
                {formatTemp(bandMin[i], settings.tempUnit)}–{formatTemp(bandMax[i], settings.tempUnit)}
              </span>
            )}
            {/* Precip bar rail */}
            <div
              className="w-full h-1 rounded-full overflow-hidden"
              style={{
                background: hasRain ? theme.accentSoft : "transparent",
              }}
            >
              {hasRain && (
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.max(6, barPct)}%`,
                    background: theme.accent,
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
